// src/services/apiHelpers.js

/**
 * Helper functions for API operations, including processing form data
 * for specific API endpoint payloads.
 */

// Helper to format date (ensure it handles invalid input)
const formatDate = (dateString) => {
  if (!dateString) return null;
  try {
    if (typeof dateString === "string" && dateString.length >= 8) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split("T")[0]; // Format as YYYY-MM-DD
      }
    }
    console.warn(
      `[apiHelpers formatDate] Invalid or insufficient date string: ${dateString}`
    );
    return null;
  } catch (e) {
    console.warn(
      `[apiHelpers formatDate] Error formatting date string "${dateString}":`,
      e
    );
    return null;
  }
};

// Helper to parse string/array to array, filtering empty strings/nulls
const parseStringToArray = (value, separator = ",") => {
  let result = [];
  if (Array.isArray(value)) {
    result = value
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter(Boolean); // Remove empty strings after trimming
  } else if (typeof value === "string" && value.trim()) {
    result = value
      .split(separator)
      .map((item) => item.trim())
      .filter(Boolean); // Remove empty strings after trimming
  }
  return result; // Returns empty array [] if no valid items
};

/**
 * Process form data from ProfileSetup for FINAL API submission.
 * This is used when the user manually completes the entire profile setup.
 * @param {Object} formData - Complete form data from the wizard state (e.g., ProfileContext).
 * @returns {Promise<Object>} - An object containing payloads for different API endpoints.
 */
export const processFormData = async (formData) => {
  console.log("==========================================================");
  console.log(
    "[apiHelpers processFormData] Start. Received formData:",
    formData
  );
  console.log("==========================================================");
  try {
    const profilePayload = {
      full_name:
        `${formData.account?.firstName || ""} ${
          formData.account?.lastName || ""
        }`.trim() || null,
      bio: formData.account?.bio?.trim() || null,
      headline:
        formData.account?.headline?.trim() ||
        formData.account?.profession?.trim() ||
        null,
      location: formData.account?.location?.trim() || null,
      phone_number: formData.account?.phoneNumber?.trim() || null,
      time_zone: formData.account?.timeZone || null,
      profession: formData.account?.profession?.trim() || null,
      salary_expectation: formData.preferences?.minSalary
        ? parseFloat(formData.preferences.minSalary)
        : null,
      preferred_job_titles: parseStringToArray(
        formData.preferences?.desiredJobTitles
      ),
      preferred_industries:
        formData.preferences?.preferredIndustries?.trim() || null,
      preferred_work_arrangement: formData.preferences?.workArrangement || null,
      preferred_company_size: formData.preferences?.companySize || null,
      work_values: formData.preferences?.values?.trim() || null,
      deal_breakers: parseStringToArray(formData.preferences?.dealBreakers),
      prefers_exploring_new_ideas:
        formData.skillTest?.prefers_exploring_new_ideas === true
          ? true
          : formData.skillTest?.prefers_exploring_new_ideas === false
          ? false
          : null,
      professional_growth_preference: Array.isArray(
        formData.skillTest?.professional_growth_preference
      )
        ? formData.skillTest.professional_growth_preference.join(", ")
        : typeof formData.skillTest?.professional_growth_preference === "string"
        ? formData.skillTest.professional_growth_preference.trim() || null
        : null,
      work_life_balance_priority:
        formData.skillTest?.work_life_balance_priority || null,
      high_salary_priority: formData.skillTest?.high_salary_priority || null,
      creative_freedom_priority:
        formData.skillTest?.creative_freedom_priority || null,
      social_impact_priority:
        formData.skillTest?.social_impact_priority || null,
      career_advancement_priority:
        formData.skillTest?.career_advancement_priority || null,
      free_work_preference:
        formData.skillTest?.free_work_preference?.trim() || null,
      problem_solving_approach:
        formData.skillTest?.problem_solving_approach || null,
      notification_preferences:
        formData.skillTest?.notification_preferences || null,
      is_complete:
        formData.isProfileComplete === true
          ? true
          : formData.isProfileComplete === false
          ? false
          : null,
    };

    for (const key in profilePayload) {
      if (profilePayload[key] === undefined || profilePayload[key] === "") {
        profilePayload[key] = null;
      }
    }
    console.log(
      "[apiHelpers processFormData] Processed profilePayload:",
      JSON.stringify(profilePayload, null, 2)
    );

    const skillNames = Array.isArray(formData.skills?.skills)
      ? formData.skills.skills.filter(Boolean).map((s) => s.trim())
      : [];
    const toolNames = Array.isArray(formData.skills?.tools)
      ? formData.skills.tools.filter(Boolean).map((t) => t.trim())
      : [];
    const licenseNames = Array.isArray(formData.skills?.licenses)
      ? formData.skills.licenses.filter(Boolean).map((l) => l.trim())
      : [];

    console.log(
      "[apiHelpers processFormData] Extracted skillNames:",
      skillNames,
      "toolNames:",
      toolNames,
      "licenseNames:",
      licenseNames
    );

    return {
      profilePayload,
      skillNames,
      toolNames,
      licenseNames,
    };
  } catch (error) {
    console.error("[apiHelpers processFormData] Error:", error);
    throw new Error(
      `Failed to process form data for API submission: ${error.message}`
    );
  }
};

/**
 * Maps profile form data to the API model format
 * @param {Object} profileData - Frontend form data
 * @returns {Object} - Formatted data for the backend API
 */
export const mapProfileData = (profileData) => {
  return {
    full_name:
      profileData.firstName && profileData.lastName
        ? `${profileData.firstName} ${profileData.lastName}`
        : profileData.full_name || "",
    bio: profileData.bio || "",
    headline: profileData.headline || profileData.profession || "",
    profile_picture: profileData.photoUrl || null,
    location: profileData.location || "",
    phone_number: profileData.phoneNumber || "",

    // Profile setup fields
    profession: profileData.profession || "",
    salary_expectation: profileData.minSalary
      ? parseFloat(profileData.minSalary)
      : null,
    preferred_job_titles: Array.isArray(profileData.desiredJobTitles)
      ? profileData.desiredJobTitles
      : (profileData.desiredJobTitles || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
    time_zone: profileData.timeZone || "",
    preferred_industries: profileData.preferredIndustries || "",
    preferred_work_arrangement: profileData.workArrangement || "",
    preferred_company_size: profileData.companySize || "",
    work_values: profileData.values || "",
    deal_breakers: Array.isArray(profileData.dealBreakers)
      ? profileData.dealBreakers
      : [],
    highest_education_level: profileData.educationLevel || "",
    licenses: Array.isArray(profileData.licenses) ? profileData.licenses : [],
    tools_software: Array.isArray(profileData.tools) ? profileData.tools : [],
  };
};

/**
 * Maps education form data to the API model format
 * @param {Object} educationData - Frontend form data
 * @returns {Object} - Formatted data for the backend API
 */
export const mapEducationData = (educationData) => {
  return {
    institution_id: educationData.institution_id || null,
    degree: educationData.degree || "",
    field_of_study: educationData.fieldOfStudy || "",
    start_date: educationData.startDate
      ? new Date(educationData.startDate).toISOString()
      : new Date().toISOString(),
    end_date: educationData.endDate
      ? new Date(educationData.endDate).toISOString()
      : null,
    is_current: educationData.endDate ? false : true,
    description: educationData.description || "",
  };
};

/**
 * Maps experience form data to the API model format
 * @param {Object} experienceData - Frontend form data
 * @returns {Object} - Formatted data for the backend API
 */
export const mapExperienceData = (experienceData) => {
  return {
    company: experienceData.companyName || experienceData.company || "",
    title: experienceData.jobTitle || experienceData.title || "",
    location: experienceData.location || "",
    employment_type: experienceData.employmentType || "Full-time",
    start_date: experienceData.startDate
      ? new Date(experienceData.startDate).toISOString()
      : new Date().toISOString(),
    end_date: experienceData.endDate
      ? new Date(experienceData.endDate).toISOString()
      : null,
    is_current: experienceData.is_current || !experienceData.endDate,
    description: experienceData.description || "",
    achievements: Array.isArray(experienceData.achievements)
      ? experienceData.achievements
      : [],
    responsibilities: Array.isArray(experienceData.responsibilities)
      ? experienceData.responsibilities
      : [],
  };
};

/**
 * Maps skill form data to the API model format
 * @param {Object} skillData - Frontend form data
 * @returns {Object} - Formatted data for the backend API
 */
export const mapSkillData = (skillData) => {
  return {
    name: skillData.name || "",
    category: skillData.category || null,
  };
};

/**
 * Maps user skill form data to the API model format
 * @param {Object} userSkillData - Frontend form data
 * @returns {Object} - Formatted data for the backend API
 */
export const mapUserSkillData = (userSkillData) => {
  return {
    skill_id: userSkillData.skill_id || null,
    proficiency_level: userSkillData.proficiency_level || 3, // Default to middle level
  };
};

/**
 * Creates FormData object for document upload (resume, cover letter, etc.).
 * @param {File} fileObject - The file to upload.
 * @param {string | null} titleForApi - Document title (defaults to file name).
 * @param {string} documentTypeForApi - Type ('resume', 'cover_letter', 'portfolio', 'other').
 * @param {boolean} isPrimaryFlag - Whether this is the primary document of its type.
 * @returns {FormData} - FormData ready for API upload.
 * @throws {Error} - If the file object is invalid.
 */
export const createDocumentFormData = (
  fileObject,
  titleForApi,
  documentTypeForApi = "resume",
  isPrimaryFlag = false
) => {
  console.log("##########################################################");
  console.log("[apiHelpers createDocumentFormData] Function called.");
  console.log(
    "[apiHelpers createDocumentFormData] Received fileObject:",
    fileObject
  );
  if (fileObject) {
    console.log(
      "[apiHelpers createDocumentFormData] fileObject.name:",
      fileObject.name
    );
    console.log(
      "[apiHelpers createDocumentFormData] fileObject.size:",
      fileObject.size
    );
    console.log(
      "[apiHelpers createDocumentFormData] fileObject.type:",
      fileObject.type
    );
    console.log(
      "[apiHelpers createDocumentFormData] fileObject instanceof File:",
      fileObject instanceof File
    );
  } else {
    console.log(
      "[apiHelpers createDocumentFormData] fileObject is null or undefined."
    );
  }
  console.log("##########################################################");

  const formData = new FormData();
  if (!(fileObject instanceof File)) {
    console.error(
      "[apiHelpers createDocumentFormData] FATAL: fileObject is NOT a File instance. Received:",
      fileObject,
      "Type:",
      typeof fileObject
    );
    throw new Error(
      "Invalid file object passed to createDocumentFormData. Cannot append to FormData."
    );
  }

  formData.append("file", fileObject, fileObject.name);
  formData.append("title", titleForApi || fileObject.name);
  formData.append("document_type", documentTypeForApi);
  formData.append("is_primary", isPrimaryFlag ? "true" : "false");

  console.log(
    `[apiHelpers createDocumentFormData] Successfully appended. FormData for ${documentTypeForApi}:`,
    `File: ${fileObject.name} (size: ${fileObject.size}, type: ${fileObject.type})`,
    `Title: ${titleForApi || fileObject.name}`,
    `DocType: ${documentTypeForApi}`,
    `IsPrimary: ${isPrimaryFlag}`
  );
  console.log(
    "[apiHelpers createDocumentFormData] All FormData Keys:",
    Array.from(formData.keys())
  );

  return formData;
};

/**
 * Creates FormData object for profile picture upload.
 * @param {File} fileObject - The image file.
 * @returns {FormData}
 */
export const createProfilePictureFormData = (fileObject) => {
  console.log("##########################################################");
  console.log("[apiHelpers createProfilePictureFormData] Function called.");
  console.log(
    "[apiHelpers createProfilePictureFormData] Received fileObject:",
    fileObject
  );
  if (fileObject) {
    console.log(
      "[apiHelpers createProfilePictureFormData] fileObject.name:",
      fileObject.name
    );
    console.log(
      "[apiHelpers createProfilePictureFormData] fileObject.size:",
      fileObject.size
    );
    console.log(
      "[apiHelpers createProfilePictureFormData] fileObject.type:",
      fileObject.type
    );
    console.log(
      "[apiHelpers createProfilePictureFormData] fileObject instanceof File:",
      fileObject instanceof File
    );
  } else {
    console.log(
      "[apiHelpers createProfilePictureFormData] fileObject is null or undefined."
    );
  }
  console.log("##########################################################");

  if (!(fileObject instanceof File)) {
    console.error(
      "[apiHelpers createProfilePictureFormData] FATAL: fileObject is NOT a File. Received:",
      fileObject,
      "Type:",
      typeof fileObject
    );
    throw new Error(
      "Invalid file object for profile picture. Expected a File object."
    );
  }
  const formData = new FormData();
  formData.append("profile_picture", fileObject, fileObject.name);

  console.log(
    "[apiHelpers createProfilePictureFormData] Creating FormData for profile picture:",
    `File: ${fileObject.name} (size: ${fileObject.size}, type: ${fileObject.type})`
  );
  console.log(
    "[apiHelpers createProfilePictureFormData] All FormData Keys:",
    Array.from(formData.keys())
  );
  return formData;
};
